import type { ReactNode } from 'react';
import {
    ConnectionsSection,
    EMS,
    ITSetup,
    Insurance,
    InventorySettings,
    Locations,
    OEM,
    Ordering,
    Quoting,
    Rekey,
    Users,
    Vendors,
    Subscription,
} from '../../../components/settings';
import type { LocationEntry, Section, SettingsResponse, useGetSettings } from '../../../hooks/useGetSettings';
import type { SessionContextType } from '../../../contexts/SessionContext';
import BetaSection from './BetaSection';
import NotificationsSection from './NotificationsSection';
import ProfileSection from './ProfileSection';
import SelectionSection from './selection/SelectionSection';
import NotesSection from './selection/NotesSection';
import PartTypesSection from './selection/PartTypesSection';
import PartAllowancesSection from './selection/PartAllowancesSection';
import InvoicingGeneralSection from './invoicing/InvoicingGeneralSection';
import EmailForwardingSection from './invoicing/EmailForwardingSection';
import FlagsSection from './invoicing/FlagsSection';
import TaxesSection from './invoicing/TaxesSection';
import MarkupsSection from './invoicing/MarkupsSection';
import DiscountsSection from './invoicing/DiscountsSection';
import PermissionsSection from './permissions/PermissionsSection';
import YaxleTeamSection from './yaxleTeam/YaxleTeamSection';

export interface SectionRenderContext {
    settings: SettingsResponse;
    /** The settings query's refetch, which sections call after their own saves. */
    refetch: ReturnType<typeof useGetSettings>['refetch'];
    session: SessionContextType | undefined;
}

const allSections = (settings: SettingsResponse): Section[] => [
    ...(settings.topSection?.sections ?? []),
    ...(settings.bottomSection?.sections ?? []),
];

/** Look up an API section by key, narrowed to its concrete shape. */
export const findSection = <K extends Section['key']>(
    settings: SettingsResponse,
    key: K,
): Extract<Section, { key: K }> | undefined =>
    allSections(settings).find((s): s is Extract<Section, { key: K }> => s.key === key);

const getLocations = (settings: SettingsResponse): LocationEntry[] =>
    findSection(settings, 'location')?.data ?? [];

/**
 * The states the org actually operates in, from its locations. Several
 * invoicing screens ask "which states does this apply in", and every one of
 * them means these, so the list is derived once here.
 */
export const organizationStatesOf = (settings: SettingsResponse): string[] =>
    Array.from(
        new Set(
            getLocations(settings)
                .map((location) => location.state?.trim().toUpperCase())
                .filter((state): state is string => !!state),
        ),
    ).sort();

/**
 * Maps a nav entry id to its rendered section. The registry in settingsNav.ts
 * decides what appears; this decides what each one renders and how its props
 * are assembled from the composite settings response.
 */
export const renderSettingsSection = (id: string, ctx: SectionRenderContext): ReactNode => {
    const { settings, refetch, session } = ctx;

    switch (id) {
        case 'profile':
            return <ProfileSection />;
        case 'notifications':
            return <NotificationsSection />;
        case 'users': {
            const section = findSection(settings, 'user');
            if (!section) return null;
            return <Users users={section.data} refetch={refetch} />;
        }
        case 'permissions': {
            const section = findSection(settings, 'user');
            if (!section) return null;
            return <PermissionsSection defaultPermissions={section.defaultPermission} />;
        }
        case 'locations': {
            const section = findSection(settings, 'location');
            if (!section) return null;
            return (
                <Locations
                    locations={section.data}
                    refetch={refetch}
                    setupType={findSection(settings, 'it-setup')?.data.setupType}
                />
            );
        }
        case 'connections':
            return <ConnectionsSection />;
        case 'desktop-app': {
            const section = findSection(settings, 'it-setup');
            if (!section) return null;
            return (
                <ITSetup data={section.data} locations={getLocations(settings)} refetch={refetch} />
            );
        }
        case 'subscription': {
            const section = findSection(settings, 'subscriptions');
            if (!section) return null;
            return <Subscription data={section.data} />;
        }
        case 'vendors': {
            const section = findSection(settings, 'vendor');
            if (!section) return null;
            return (
                <Vendors
                    vendors={section.data}
                    refetch={refetch}
                    isReadOnly={session?.organizationMode === 'invoicing'}
                />
            );
        }
        case 'drp': {
            const section = findSection(settings, 'insurance');
            if (!section) return null;
            // Inline edits stage in the settings draft, which owns the save.
            return <Insurance insurance={section.data} />;
        }
        case 'oem': {
            const section = findSection(settings, 'oem');
            if (!section) return null;
            return <OEM oems={section.data} oemOptions={settings.oemOptions} refetch={refetch} />;
        }
        case 'ems': {
            const section = findSection(settings, 'ems');
            if (!section) return null;
            return <EMS data={section.data} />;
        }
        case 'quoting': {
            const section = findSection(settings, 'quoting');
            if (!section) return null;
            const { hasCl, hasPt, ...quotingData } = section.data;
            return (
                <Quoting
                    data={quotingData}
                    hasCl={hasCl}
                    hasPt={hasPt}
                    oemOptions={settings.oemOptions}
                    // Per-make minimum quoting times are stored on the oem
                    // subdocument, but they belong beside the quoting window.
                    oems={findSection(settings, 'oem')?.data ?? []}
                />
            );
        }
        case 'selection':
        case 'notes':
        case 'part-types':
        case 'part-allowances': {
            const section = findSection(settings, 'selection');
            if (!section) return null;
            const selectionProps = {
                data: section.data,
                oemOptions: settings.oemOptions,
                insuranceOptions: findSection(settings, 'insurance')?.data ?? [],
                nonDrpInsuranceOptions: settings.nonDrpInsuranceOptions ?? [],
                refetch,
            };
            switch (id) {
                case 'notes':
                    return <NotesSection {...selectionProps} />;
                case 'part-types':
                    return (
                        <PartTypesSection
                            {...selectionProps}
                            locations={getLocations(settings).map((location) => ({
                                _id: location._id,
                                title: location.title,
                            }))}
                        />
                    );
                case 'part-allowances':
                    return <PartAllowancesSection {...selectionProps} />;
                default:
                    return <SelectionSection {...selectionProps} />;
            }
        }
        case 'ordering': {
            const section = findSection(settings, 'ordering');
            if (!section) return null;
            return (
                <Ordering
                    data={section.data}
                    managementSystem={section.managementSystem}
                    // Per-make margin thresholds are stored on the oem
                    // subdocument, but they belong beside the org-wide ones.
                    oems={findSection(settings, 'oem')?.data ?? []}
                    oemOptions={settings.oemOptions}
                    locations={getLocations(settings).map((location) => ({
                        _id: location._id,
                        title: location.title,
                    }))}
                />
            );
        }
        case 'rekeying': {
            const section = findSection(settings, 'rekey');
            if (!section) return null;
            return (
                <Rekey
                    data={section.data}
                    managementSystem={section.managementSystem}
                    oemOptions={settings.oemOptions}
                />
            );
        }
        case 'beta':
            return (
                <BetaSection
                    // The receive opt-in date rides along on the ordering
                    // section, which is where the toggle used to live.
                    receivingEnabledAt={findSection(settings, 'ordering')?.receivingEnabledAt ?? null}
                    nexOrdering={findSection(settings, 'ordering')?.nexOrdering ?? null}
                    nexIntegrationAvailable={findSection(settings, 'ordering')?.nexIntegrationAvailable ?? false}
                    locations={getLocations(settings)}
                />
            );
        case 'yaxle-team':
            return <YaxleTeamSection />;
        case 'inventory':
            return (
                <InventorySettings
                    inventory={settings.inventory}
                    locations={getLocations(settings)}
                />
            );
        case 'invoicing-general':
        case 'invoicing-email-forwarding':
        case 'invoicing-flags':
        case 'invoicing-taxes':
        case 'invoicing-markups':
        case 'invoicing-discounts': {
            const section = findSection(settings, 'invoicing');
            if (!section) return null;
            const invoicingProps = {
                data: section.data,
                organizationStates: organizationStatesOf(settings),
                locations: getLocations(settings),
            };
            switch (id) {
                case 'invoicing-email-forwarding':
                    return <EmailForwardingSection {...invoicingProps} />;
                case 'invoicing-flags':
                    return <FlagsSection {...invoicingProps} />;
                case 'invoicing-taxes':
                    return <TaxesSection {...invoicingProps} />;
                case 'invoicing-markups':
                    return <MarkupsSection {...invoicingProps} />;
                case 'invoicing-discounts':
                    return <DiscountsSection {...invoicingProps} />;
                default:
                    return <InvoicingGeneralSection {...invoicingProps} />;
            }
        }
        default:
            return null;
    }
};
